import type { GameStore } from '../../store/GameStore';
import { formatCurrency, formatNumber } from '../../i18n/i18n';

/**
 * Company stats panel — shows company overview, finances, products.
 */
export class CompanyPanel {
  private element: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private store: GameStore;
  private visible: boolean = false;

  constructor(parent: HTMLElement, store: GameStore) {
    this.store = store;

    this.element = document.createElement('div');
    this.element.className = 'panel';
    this.element.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;width:300px;transform:translateX(100%);transition:transform var(--transition-normal);pointer-events:auto;z-index:10;';

    const header = document.createElement('div');
    header.className = 'panel__header';
    const title = document.createElement('span');
    title.textContent = 'Company';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.element.appendChild(header);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel__body';
    this.element.appendChild(this.bodyEl);

    parent.appendChild(this.element);
  }

  show(): void {
    this.visible = true;
    this.element.style.transform = 'translateX(0)';
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.element.style.transform = 'translateX(100%)';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  render(): void {
    if (!this.visible) return;
    this.bodyEl.innerHTML = '';
    const state = this.store.getState();

    const section = (title: string): HTMLDivElement => {
      const el = document.createElement('div');
      el.style.cssText = 'margin-bottom:16px;';
      const h = document.createElement('div');
      h.textContent = title;
      h.style.cssText = 'font-size:13px;font-weight:600;margin-bottom:6px;border-bottom:1px solid var(--border-color);padding-bottom:4px;';
      el.appendChild(h);
      return el;
    };

    const stat = (label: string, value: string, color?: string): HTMLDivElement => {
      const el = document.createElement('div');
      el.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;padding:2px 0;';
      const l = document.createElement('span');
      l.textContent = label;
      l.style.color = 'var(--color-text-secondary)';
      const v = document.createElement('span');
      v.textContent = value;
      v.style.cssText = `font-family:var(--font-pixel);font-weight:600;${color ? `color:${color};` : ''}`;
      el.appendChild(l);
      el.appendChild(v);
      return el;
    };

    // Overview
    const overview = section('Overview');
    overview.appendChild(stat('Company', state.company.name));
    overview.appendChild(stat('Level', String(state.company.level), 'var(--color-xp)'));
    overview.appendChild(stat('Reputation', `${Math.round(state.company.reputation)}/100`));
    overview.appendChild(stat('Fans', formatNumber(state.company.fans)));
    this.bodyEl.appendChild(overview);

    // Finances
    const finances = section('Finances');
    finances.appendChild(stat('Funds', formatCurrency(state.company.funds), state.company.funds < 0 ? 'var(--color-danger)' : 'var(--color-funds)'));
    finances.appendChild(stat('Total Revenue', formatCurrency(state.company.totalRevenue)));
    finances.appendChild(stat('Total Expenses', formatCurrency(state.company.totalExpenses)));
    const profit = state.company.totalRevenue - state.company.totalExpenses;
    finances.appendChild(stat('Net Profit', formatCurrency(profit), profit >= 0 ? 'var(--color-success)' : 'var(--color-danger)'));
    if (state.company.weeksInDebt > 0) {
      finances.appendChild(stat('Weeks in Debt', `${state.company.weeksInDebt} / ${8}`, 'var(--color-danger)'));
    }
    this.bodyEl.appendChild(finances);

    // Staff summary
    const staffSec = section('Staff');
    staffSec.appendChild(stat('Count', `${state.staff.length} / ${state.office.maxStaff}`));
    staffSec.appendChild(stat('Teams', `${state.teams.length} / ${state.office.maxTeams}`));
    const avgMorale = state.staff.length > 0
      ? Math.round(state.staff.reduce((s, st) => s + st.morale, 0) / state.staff.length)
      : 0;
    staffSec.appendChild(stat('Avg Morale', `${avgMorale}%`, avgMorale > 60 ? 'var(--color-morale-high)' : avgMorale > 30 ? 'var(--color-morale-mid)' : 'var(--color-morale-low)'));
    this.bodyEl.appendChild(staffSec);

    // Products
    const products = section(`Products (${state.products.length})`);
    if (state.products.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No products released yet.';
      empty.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
      products.appendChild(empty);
    } else {
      // Show last 8 products with detail
      const recent = state.products.slice(-8).reverse();
      for (const product of recent) {
        const card = document.createElement('div');
        card.style.cssText = 'padding:4px 0;border-bottom:1px solid var(--border-color);';

        const topRow = document.createElement('div');
        topRow.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;';
        const nameEl = document.createElement('span');
        nameEl.textContent = product.name + (product.isHallOfFame ? ' ★' : '');
        nameEl.style.cssText = `font-weight:600;${product.isHallOfFame ? 'color:var(--color-warning);' : ''}`;
        const scoreEl = document.createElement('span');
        scoreEl.textContent = `${product.averageReview.toFixed(1)}/10`;
        scoreEl.style.cssText = `font-family:var(--font-pixel);color:${product.averageReview >= 8 ? 'var(--color-success)' : product.averageReview >= 5 ? 'var(--color-text-primary)' : 'var(--color-danger)'};`;
        topRow.appendChild(nameEl);
        topRow.appendChild(scoreEl);
        card.appendChild(topRow);

        const detailRow = document.createElement('div');
        detailRow.style.cssText = 'display:flex;justify-content:space-between;font-size:9px;color:var(--color-text-muted);';
        detailRow.innerHTML = `<span>${product.category} · ${product.platform} · ${product.size}</span><span style="font-family:var(--font-pixel);color:var(--color-funds);">${formatCurrency(product.totalRevenue)}</span>`;
        card.appendChild(detailRow);

        const scoresRow = document.createElement('div');
        scoresRow.style.cssText = 'font-size:9px;color:var(--color-text-muted);';
        scoresRow.textContent = `Reviews: ${product.reviewScores.map(s => s.toFixed(1)).join(', ')} · Sales: ${formatNumber(product.totalSales)}`;
        card.appendChild(scoresRow);

        products.appendChild(card);
      }

      products.appendChild(stat('Hall of Fame', `${state.company.hallOfFameCount} / ${state.products.length}`));
    }
    this.bodyEl.appendChild(products);

    // Weekly Outgoings
    const outgoings = section('Weekly Outgoings');
    const totalSalaries = state.staff.reduce((s, st) => s + (st.isCEO ? 0 : st.salary), 0);
    const weeklyRent = state.office.weeklyRent;
    const loanPayments = state.loans.reduce((s, l) => s + l.weeklyPayment, 0);
    const totalWeekly = totalSalaries + weeklyRent + loanPayments;
    outgoings.appendChild(stat('Salaries', formatCurrency(totalSalaries)));
    outgoings.appendChild(stat('Rent', formatCurrency(weeklyRent)));
    if (loanPayments > 0) {
      outgoings.appendChild(stat('Loan repayments', formatCurrency(loanPayments)));
    }
    outgoings.appendChild(stat('Total/week', formatCurrency(totalWeekly), 'var(--color-danger)'));
    const runway = totalWeekly > 0 ? Math.floor(Math.max(0, state.company.funds) / totalWeekly) : 999;
    outgoings.appendChild(stat('Runway', `${runway} weeks`, runway < 4 ? 'var(--color-danger)' : 'var(--color-text-primary)'));
    this.bodyEl.appendChild(outgoings);

    // Office
    const officeSec = section('Office');
    officeSec.appendChild(stat('Level', `${state.office.level} — ${state.office.name}`));
    officeSec.appendChild(stat('Max Projects', String(state.office.maxConcurrentProjects)));
    this.bodyEl.appendChild(officeSec);
  }

  destroy(): void {
    this.element.remove();
  }
}
